'use client';

import { createRevealObserver } from '@/lib/reveal-observer';
import React, { useEffect, useRef } from 'react';
import Link from 'next/link';

const sectors = [
  'FinTech',
  'Healthcare / HealthTech',
  'Technology',
  'SaaS',
  'AI',
  'Climate / Sustainability',
  'Consumer',
  'DeepTech',
  'Financial Services',
];

const clientTypes = [
  {
    title: 'Early-Stage',
    subtitle:
      'Companies raising Pre-Seed, Seed and Series A — and selected later-stage opportunities.',
    stages: ['Pre-Seed', 'Seed', 'Series A'],
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
      </svg>
    ),
  },
  {
    title: 'Growth Companies',
    subtitle:
      'Businesses with revenue, market traction, established customers and clear expansion opportunities.',
    stages: ['Revenue-generating', 'Market traction', 'Expansion capital'],
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
        <polyline points="17 6 23 6 23 12" />
      </svg>
    ),
  },
  {
    title: 'Sector Focus',
    subtitle:
      'Depending on investor appetite and mandate, opportunities across high-growth sectors.',
    stages: sectors?.slice(0, 4),
    icon: (
      <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
      </svg>
    ),
  },
];

export default function WhoWeWorkWithSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = createRevealObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.scroll-fade').forEach((el) => {
              el.classList.add('visible');
            });
          }
        });
      },
      { rootMargin: '0px 0px -60px 0px', threshold: 0.05 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="who-we-work-with"
      className="py-24 px-6 border-t border-primary/10 bg-secondary/20"
    >
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto mb-16">
          <p className="section-label scroll-fade mb-5">Who We Work With</p>
          <h2 className="text-section-title font-sans font-semibold text-foreground scroll-fade scroll-fade-delay-1 mb-4">
            Built for ambitious companies{' '}
            <span className="font-serif italic text-primary">at moments that matter.</span>
          </h2>
          <p className="text-sm text-muted-foreground font-light leading-relaxed scroll-fade scroll-fade-delay-2">
            Aregn Capital works with companies across different stages and sectors where external
            capital can accelerate growth.
          </p>
        </div>

        {/* BENTO AUDIT: 3 client type cards */}
        {/* Row 1: [col-1: Early-Stage cs-1] [col-2: Growth cs-1] [col-3: Sector cs-1] */}
        {/* Placed 3/3 ✓ */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          {clientTypes?.map((type, i) => (
            <div
              key={type?.title}
              className={`bento-card rounded-3xl p-8 flex flex-col scroll-fade ${i === 1 ? 'border-primary/25 bg-primary/5' : ''}`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <div className="w-12 h-12 rounded-xl border border-primary/25 flex items-center justify-center text-primary mb-6 bg-primary/8">
                {type?.icon}
              </div>
              <h3 className="text-xl font-semibold text-foreground mb-3">{type?.title}</h3>
              <p className="text-sm text-muted-foreground font-light leading-relaxed mb-6 flex-1">
                {type?.subtitle}
              </p>
              <div className="flex flex-wrap gap-2">
                {type?.stages?.map((s) => (
                  <span
                    key={s}
                    className="text-xs px-3 py-1.5 rounded-full border border-primary/20 text-primary/80 bg-primary/5"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Sector tags */}
        <div className="scroll-fade">
          <p className="text-xs font-semibold tracking-widest uppercase text-muted-foreground mb-4 text-center">
            Sector Coverage
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {sectors?.map((sector) => (
              <span
                key={sector}
                className="text-xs px-4 py-2 rounded-full border border-primary/15 text-muted-foreground hover:border-primary/35 hover:text-foreground transition-all cursor-default"
              >
                {sector}
              </span>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-12 flex justify-center scroll-fade">
          <Link
            href="/contact"
            className="px-8 py-4 rounded-full bg-primary text-primary-foreground text-sm font-semibold tracking-widest uppercase hover:bg-accent transition-all duration-300"
          >
            Submit Your Company
          </Link>
        </div>
      </div>
    </section>
  );
}
